import {
  Component,
  ChangeDetectionStrategy,
  input,
  output,
  computed,
  forwardRef,
  signal,
  inject,
} from '@angular/core';

// Unique ID counter for input components
let inputIdCounter = 0;
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

/**
 * Input types supported by the design system
 */
export type InputType =
  | 'text'
  | 'email'
  | 'password'
  | 'number'
  | 'tel'
  | 'url'
  | 'search'
  | 'date'
  | 'time'
  | 'datetime-local';

/**
 * Input sizes available in the design system
 */
export type InputSize = 'sm' | 'md' | 'lg';

@Component({
  selector: 'app-atom-input',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './input.component.html',
  styleUrls: ['./input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputComponent),
      multi: true,
    },
  ],
})
export class InputComponent implements ControlValueAccessor {
  // Generate unique ID for this component instance
  private readonly uniqueId = `input-${++inputIdCounter}`;

  // Signal-based inputs (Angular 19)
  type = input<InputType>('text');
  showPasswordToggle = input<boolean>(false);

  // Internal state for password visibility
  passwordVisible = signal<boolean>(false);

  // Computed actual type (for password toggle feature)
  actualType = computed(() => {
    if (this.type() === 'password' && this.showPasswordToggle() && this.passwordVisible()) {
      return 'text';
    }
    return this.type();
  });

  // Toggle password visibility
  togglePasswordVisibility(): void {
    this.passwordVisible.update(v => !v);
  }

  // Original type input
  placeholder = input<string>('');
  disabledInput = input(false, { alias: 'disabled' });
  private _disabledFromCVA = signal<boolean>(false);
  disabled = computed(() => this.disabledInput() || this._disabledFromCVA());
  required = input<boolean>(false);
  readonly = input<boolean>(false);
  error = input<boolean>(false);
  errorMessage = input<string>('');
  label = input<string>('');
  helperText = input<string>('');
  id = input<string>('');
  name = input<string>('');

  // Computed ID that falls back to unique ID if not provided
  readonly inputId = computed(() => this.id() || this.uniqueId);
  size = input<InputSize>('md');
  additionalClasses = input<string>('');
  autocomplete = input<string>('off');
  maxlength = input<number | null>(null);
  min = input<string | number | null>(null);
  max = input<string | number | null>(null);
  step = input<string | number | null>(null);
  pattern = input<string>('');

  // Signal-based output
  inputBlur = output<void>();
  inputFocus = output<void>();

  // Internal value signal for ControlValueAccessor
  internalValue = signal<string>('');

  // ControlValueAccessor callbacks
  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};

  // Computed classes for the wrapper
  wrapperClasses = computed(() => {
    const classes = ['form-group'];
    if (this.additionalClasses()) {
      classes.push(this.additionalClasses());
    }
    return classes.join(' ');
  });

  // Computed classes for the input element
  inputClasses = computed(() => {
    const classes: string[] = [
      'w-full',
      'border-2',
      'rounded-lg',
      'transition-all',
      'duration-200',
      'focus:outline-none',
      'focus:ring-2',
      'focus:ring-offset-0',
    ];

    // Size classes
    const sizeMap: Record<InputSize, string> = {
      sm: 'px-3 py-1.5 text-sm',
      md: 'px-4 py-2.5 text-sm',
      lg: 'px-4 py-3 text-base',
    };
    classes.push(sizeMap[this.size()]);

    // Error state
    if (this.error()) {
      classes.push(
        'border-danger-500',
        'focus:ring-danger-500/20',
        'focus:border-danger-500',
        'bg-red-50/50'
      );
    } else {
      classes.push(
        'border-gray-200',
        'focus:ring-primary-500/20',
        'focus:border-primary-500',
        'bg-white',
        'hover:border-gray-300'
      );
    }

    // Disabled/readonly state
    if (this.disabled() || this.readonly()) {
      classes.push('!bg-gray-100', 'cursor-not-allowed', 'opacity-60');
    }

    return classes.join(' ');
  });

  // Computed label classes
  labelClasses = computed(() => {
    return 'block text-sm font-medium text-gray-700 mb-1.5';
  });

  // Computed helper text classes
  helperClasses = computed(() => {
    return 'mt-1.5 text-xs text-gray-500';
  });

  // Computed error classes
  errorClasses = computed(() => {
    return 'mt-1.5 text-xs text-danger-600 flex items-center gap-1';
  });

  // Show toggle icon when password type and toggle enabled and has value
  showToggleIcon = computed(() => {
    return (
      this.type() === 'password' && this.showPasswordToggle() && this.internalValue()?.length > 0
    );
  });

  // Input needs padding for toggle icon
  needsTogglePadding = computed(() => {
    return this.type() === 'password' && this.showPasswordToggle();
  });

  // ControlValueAccessor implementation
  writeValue(value: string): void {
    this.internalValue.set(value ?? '');
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this._disabledFromCVA.set(isDisabled);
  }

  // Event handlers
  onInputChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    const value = target.value;
    this.internalValue.set(value);
    this.onChange(value);
  }

  onBlur(): void {
    this.onTouched();
    this.inputBlur.emit();
  }

  onFocus(): void {
    this.inputFocus.emit();
  }
}
